import React, { useState } from "react";
import { ExternalLink, Loader2, CheckCircle, XCircle } from "lucide-react";
import { useAlgorandDonation } from "../hooks/useAlgorandDonation";
import AnimatedButton from "./AnimatedButton";
import { useLanguageStore } from "../store/languageStore";

const translations = {
  EN: {
    title: "Support RightNowAid",
    desc: "Your donation helps us continue providing life-saving assistance to those in need.",
    donate: "Donate Now",
    powered: "Powered by Algorand",
    amountLabel: "Donation Amount (ALGO)",
    connecting: "Connecting...",
    processing: "Processing...",
    tryAgain: "Try Again",
    donated: "Donated!",
    thankYou: "Thank you for your donation of",
    viewOnExplorer: "View on AlgoExplorer",
    transactionId: "Transaction ID:",
    testnetWarning: "* This uses Algorand TestNet. No real money is required.",
  },
  ES: {
    title: "Apoya a RightNowAid",
    desc: "Tu donación nos ayuda a seguir brindando asistencia que salva vidas a quienes la necesitan.",
    donate: "Donar Ahora",
    powered: "Impulsado por Algorand",
    amountLabel: "Cantidad de Donación (ALGO)",
    connecting: "Conectando...",
    processing: "Procesando...",
    tryAgain: "Intentar de Nuevo",
    donated: "¡Donado!",
    thankYou: "¡Gracias por tu donación de",
    viewOnExplorer: "Ver en AlgoExplorer",
    transactionId: "ID de Transacción:",
    testnetWarning: "* Esto usa Algorand TestNet. No se requiere dinero real.",
  },
  ZH: {
    title: "支持 RightNowAid",
    desc: "您的捐赠帮助我们继续为需要帮助的人提供救生援助。",
    donate: "立即捐赠",
    powered: "由 Algorand 提供支持",
    amountLabel: "捐赠金额 (ALGO)",
    connecting: "连接中...",
    processing: "处理中...",
    tryAgain: "重试",
    donated: "已捐赠！",
    thankYou: "感谢您的捐赠",
    viewOnExplorer: "在 AlgoExplorer 上查看",
    transactionId: "交易 ID:",
    testnetWarning: "* 这使用 Algorand 测试网。不需要真实货币。",
  },
  VI: {
    title: "Hỗ Trợ RightNowAid",
    desc: "Đóng góp của bạn giúp chúng tôi tiếp tục cung cấp hỗ trợ cứu mạng cho những người cần giúp đỡ.",
    donate: "Quyên Góp Ngay",
    powered: "Được hỗ trợ bởi Algorand",
    amountLabel: "Số Tiền Quyên Góp (ALGO)",
    connecting: "Đang kết nối...",
    processing: "Đang xử lý...",
    tryAgain: "Thử Lại",
    donated: "Đã Quyên Góp!",
    thankYou: "Cảm ơn bạn đã quyên góp",
    viewOnExplorer: "Xem trên AlgoExplorer",
    transactionId: "ID Giao Dịch:",
    testnetWarning: "* Điều này sử dụng Algorand TestNet. Không yêu cầu tiền thật.",
  },
  FIL: {
    title: "Suportahan ang RightNowAid",
    desc: "Ang iyong donasyon ay tumutulong sa amin na patuloy na magbigay ng tulong na nagliligtas ng buhay sa mga nangangailangan.",
    donate: "Mag-donate Ngayon",
    powered: "Pinapagana ng Algorand",
    amountLabel: "Halaga ng Donasyon (ALGO)",
    connecting: "Kumokonekta...",
    processing: "Pinoproseso...",
    tryAgain: "Subukan Muli",
    donated: "Na-donate!",
    thankYou: "Salamat sa iyong donasyon na",
    viewOnExplorer: "Tingnan sa AlgoExplorer",
    transactionId: "ID ng Transaksyon:",
    testnetWarning: "* Ito ay gumagamit ng Algorand TestNet. Hindi kailangan ng tunay na pera.",
  },
  KO: {
    title: "RightNowAid 지원하기",
    desc: "여러분의 기부는 필요한 사람들에게 생명을 구하는 지원을 계속 제공하는 데 도움이 됩니다.",
    donate: "지금 기부하기",
    powered: "Algorand 지원",
    amountLabel: "기부 금액 (ALGO)",
    connecting: "연결 중...",
    processing: "처리 중...",
    tryAgain: "다시 시도",
    donated: "기부 완료!",
    thankYou: "기부해 주셔서 감사합니다",
    viewOnExplorer: "AlgoExplorer에서 보기",
    transactionId: "거래 ID:",
    testnetWarning: "* 이것은 Algorand 테스트넷을 사용합니다. 실제 돈이 필요하지 않습니다.",
  },
  AR: {
    title: "دعم RightNowAid",
    desc: "تبرعك يساعدنا على الاستمرار في تقديم المساعدة المنقذة للحياة لمن يحتاجها.",
    donate: "تبرع الآن",
    powered: "مدعوم بواسطة Algorand",
    amountLabel: "مبلغ التبرع (ALGO)",
    connecting: "جاري الاتصال...",
    processing: "جاري المعالجة...",
    tryAgain: "حاول مرة أخرى",
    donated: "تم التبرع!",
    thankYou: "شكراً لتبرعك بمبلغ",
    viewOnExplorer: "عرض على AlgoExplorer",
    transactionId: "معرف المعاملة:",
    testnetWarning: "* هذا يستخدم شبكة Algorand التجريبية. لا يلزم وجود أموال حقيقية.",
  },
};

const DonationWidget: React.FC = () => {
  const [amount, setAmount] = useState(0.1);
  const { selectedLanguage } = useLanguageStore();
  const t = translations[selectedLanguage];
  const {
    isConnecting,
    isTransacting,
    error,
    txId,
    donate: submitDonation,
    clearError,
  } = useAlgorandDonation();

  const handleDonate = async () => {
    if (error) {
      clearError();
      return;
    }

    try {
      await submitDonation(amount);
    } catch (err) {
      console.error("Donation failed:", err);
    }
  };

  const getExplorerLink = (txId: string) => {
    return `https://testnet.algoexplorer.io/tx/${txId}`;
  };

  const isLoading = isConnecting || isTransacting;

  return (
    <section id="donate" className="py-16" aria-label={t.title}>
      <div className="max-w-xl mx-auto px-4 text-center">
        <h2 className="text-3xl font-bold mb-4">{t.title}</h2>
        <p className="mb-6 text-gray-700 dark:text-gray-300">{t.desc}</p>

        {/* Amount Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">
            {t.amountLabel}
          </label>
          <div className="flex justify-center gap-2 mb-4">
            {[0.1, 0.5, 1.0, 5.0].map((preset) => (
              <button
                key={preset}
                onClick={() => setAmount(preset)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  amount === preset
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                }`}
              >
                {preset} ALGO
              </button>
            ))}
          </div>
          <input
            type="number"
            step="0.1"
            min="0.1"
            value={amount}
            onChange={(e) => setAmount(parseFloat(e.target.value) || 0.1)}
            className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-center bg-white dark:bg-gray-700"
          />
        </div>

        {/* Donation Button */}
        <AnimatedButton
          onClick={handleDonate}
          disabled={isLoading}
          variant="primary"
          size="lg"
          className="min-w-[200px]"
          aria-label={t.donate}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {isConnecting ? t.connecting : t.processing}
            </>
          ) : error ? (
            <>
              <XCircle className="w-4 h-4" />
              {t.tryAgain}
            </>
          ) : txId ? (
            <>
              <CheckCircle className="w-4 h-4" />
              {t.donated}
            </>
          ) : (
            t.donate
          )}
        </AnimatedButton>

        {/* Success State */}
        {txId && (
          <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <p className="text-green-700 dark:text-green-300 font-medium mb-2">
              {t.thankYou} {amount} ALGO!
            </p>
            <a
              href={getExplorerLink(txId)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline text-sm"
            >
              {t.viewOnExplorer}
              <ExternalLink className="w-3 h-3" />
            </a>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
              {t.transactionId} {txId.slice(0, 8)}...{txId.slice(-8)}
            </p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
          </div>
        )}

        <p className="text-xs text-gray-500 mt-4">{t.powered}</p>

        {/* TestNet Warning */}
        <p className="text-xs text-orange-600 dark:text-orange-400 mt-2">
          {t.testnetWarning}
        </p>
      </div>
    </section>
  );
};

export default DonationWidget;
